import { useMemo } from 'react'

import { computeProgress } from '../../lib/progress'
import { usePromo } from '../../state'

/**
 * ProgressBar - Shows cart total progress toward discount levels.
 *
 * Uses theme tokens: text-color, main-color, promo-discount-bg gradient.
 * Reads all data from PromoProvider context (no props).
 */
export function ProgressBar() {
  const { total, promotionLevels } = usePromo()

  const {
    sortedLevels,
    maxLevel,
    levelReached,
    percentage,
    difference,
    allLevelsReached,
  } = useMemo(() => computeProgress(total, promotionLevels), [total, promotionLevels])

  return (
    <div className="bg-promo-discount-bg md:rounded-xl p-4 xl:p-6 font-main-font">
      {/* Top row: cart value + level */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm xl:text-base text-white/70">Valoare cos:</span>
          <span className="text-lg xl:text-xl font-bold text-main-color">
            {total} Lei
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm xl:text-base text-text-color">Nivel discount:</span>
          <span className="text-lg xl:text-xl font-bold text-custom-red,">
            {levelReached}/{maxLevel}
          </span>
        </div>
      </div>

      {/* Progress bar with level markers */}
      <div className="relative">
        {/* Track */}
        <div className="h-3 rounded-full bg-white/10 overflow-hidden">
          <div
            style={{ width: `${percentage}%` }}
            className="h-full rounded-full bg-main-color transition-all duration-500 ease-out"
            role="progressbar"
            aria-valuenow={Math.round(percentage)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Progres nivel discount"
          />
        </div>

        {/* Level markers */}
        <div className="flex justify-between mt-2">
          {sortedLevels.map((level, i) => {
            const reached = total >= level
            return (
              <span
                key={level}
                className={`text-xs xl:text-sm font-bold ${reached ? 'text-custom-red' : 'text--text-color'}`}
              >
                Nivel {i + 1}: {level} Lei
              </span>
            )
          })}
        </div>
      </div>

      {/* Bottom message */}
      {!allLevelsReached && (
        <p className="text-sm xl:text-base text-white/80 mt-3 text-center">
          Mai adauga{' '}
          <span className="font-bold text-main-color">{difference} Lei</span>
          {' '}in cos pentru urmatorul nivel de discount
        </p>
      )}
      {allLevelsReached && (
        <p className="text-sm xl:text-base text-main-color font-bold mt-3 text-center">
          Ai atins nivelul maxim de discount!
        </p>
      )}
    </div>
  )
}
